import { PieChart as PieChartIcon } from '@mui/icons-material'
import { Divider, Stack, Typography } from '@mui/material'
import { Box } from '@mui/system'
import dayjs from 'dayjs'
import { t } from 'i18next'
import { useEffect, useState } from 'react'
import { PieChart } from 'react-minimal-pie-chart'
import { useSelector } from 'react-redux'
import defaultProps from '../../../config/defaultProps'
import { useGetSumByTypeQuery } from '../../../store/redux/reducers/costSlice'
import { getCurrentMode } from '../../../store/redux/reducers/darkModeSlice'
import DateFormat from '../../../store/types/enums/DateFormat'
import { StyledContainerColumnLeft, StyledContainerRowBetween } from '../../../styles/ContainerStyles'
import { StyledMasonryTile } from '../../../styles/MasonryStyles'
import { paletteDark, paletteLight } from '../../../styles/theme-configuration/palettes'
import LoadingWrapper from '../../Loading/LoadingWrapper'
import { costTypeOptions } from '../cost/CostViewForm'

type CostPieChartContentProps = {
  date: string
}

type CostPieChartProps = {
  id: string
  value: number
  label: string
  color: string
}

const CostPieChartContent = ({ date }: CostPieChartContentProps) => {
  let formattedDate = dayjs(date).format(`${DateFormat.MonthName} • ${DateFormat.Year}`)

  const { data: costs, isLoading } = useGetSumByTypeQuery(
    {
      yearMonth: dayjs(date).format('YYYY-MM'),
      ...defaultProps,
    },
    {
      skip: !date,
    }
  )

  const [allCostZero, setAllCostZero] = useState<boolean>(true)
  const [hoveredSegment, setHoveredSegment] = useState<number | null>(null)

  useEffect(() => {
    if (costs) {
      const allCosts = Object.values(costs)
      const allCostsSum = allCosts.reduce((acc, curr) => acc + curr, 0)
      setAllCostZero(allCostsSum === 0)
    }
  }, [costs])

  const mode = useSelector(getCurrentMode)
  const palette = mode === 'light' ? paletteLight : paletteDark

  const themeColors = [
    palette.accent.blue.main,
    palette.accent.purple.main,
    palette.accent.teal.main,
    palette.primary.light ? palette.primary.light : palette.primary.main,
    palette.accent.green.main,
  ]

  const grayThemeColors = [palette.background.elevation9, palette.background.elevation8]

  const costsData: CostPieChartProps[] = costTypeOptions.map((type, index) => {
    return {
      id: type.value,
      value: costs ? costs[type.value] : 0.0,
      label: t(type.label),
      color: themeColors[index],
    }
  })

  return (
    <Stack sx={{ pt: 6 }} gap={2}>
      <StyledContainerRowBetween>
        <Box sx={{ gap: 2, display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <PieChartIcon sx={{ fontSize: 36 }} />
          <Typography color={'text.highEmphasis'} variant="h4">
            {t('components.costPieChart.title')}
          </Typography>
        </Box>
        <Box>
          <StyledContainerColumnLeft>
            <Typography color={'text.highEmphasis'}>{`${formattedDate}`}</Typography>
          </StyledContainerColumnLeft>
        </Box>
      </StyledContainerRowBetween>
      <LoadingWrapper isLoading={isLoading}>
        <Stack>
          {allCostZero ? (
            <PieChart
              data={[{ title: t('components.costPieChart.empty'), value: 1, color: grayThemeColors[0] }]}
              style={{ height: '200px' }}
              lineWidth={100}
            />
          ) : (
            <PieChart
              data={costsData.map((cost, _) => ({
                title: cost.label,
                value: cost.value,
                color: cost.color,
              }))}
              style={{ height: '250px' }}
              radius={40}
              labelPosition={110}
              lineWidth={100}
              label={({ dataIndex, dataEntry }) =>
                hoveredSegment === dataIndex
                  ? `${dataEntry.value.toFixed(2)} zł`
                  : dataEntry.value === 0
                  ? null
                  : `${Math.round(dataEntry.percentage)} %`
              }
              labelStyle={{
                fontSize: '8px',
                fill: palette.text.highEmphasis,
                pointerEvents: 'none',
                fontFamily: 'Rajdhani',
                fontWeight: 'bold',
              }}
              segmentsStyle={index => ({
                filter: hoveredSegment === index ? 'brightness(0.9)' : 'none',
              })}
              onMouseOver={(_, index) => setHoveredSegment(index)}
              onMouseOut={() => setHoveredSegment(null)}
            />
          )}
        </Stack>
      </LoadingWrapper>
      <Stack>
        <Box marginBottom={1}>
          <Typography color={'text.highEmphasis'} variant="h5">
            {t('components.costPieChart.description')}
          </Typography>
        </Box>
        <Box>
          <LoadingWrapper isLoading={isLoading}>
            {costsData.map((cost, index) => (
              <StyledMasonryTile key={index} sx={{ flexDirection: 'row', justifyContent: 'space-between', marginY: 2 }}>
                <Box sx={{ gap: 2, display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      backgroundColor: cost.color,
                    }}
                  />
                  <Typography color={'text.highEmphasis'}>{cost.label}</Typography>
                </Box>
                <Typography color={'text.mediumEmphasis'}>{cost.value.toFixed(2)} zł</Typography>
              </StyledMasonryTile>
            ))}
            <Divider />
            <Box
              sx={{
                marginY: 2,
                gap: 2,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant={'h6'} color={'text.highEmphasis'}>
                {t('components.costPieChart.total')}
              </Typography>
              <Typography color={'text.highEmphasis'} fontWeight={'bold'}>
                {costs
                  ? Object.values(costs)
                      .reduce((acc, curr) => acc + curr, 0)
                      .toFixed(2)
                  : 0.0}{' '}
                zł
              </Typography>
            </Box>
          </LoadingWrapper>
        </Box>
      </Stack>
    </Stack>
  )
}

export default CostPieChartContent
